import type { LegitimacyMarking } from '@/types/prioritization/catchballLoop';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';

export type CycleDesignerType = 'standard' | 'expedited' | 'discovery';

/** Client-side path identifier — a…z, then aa, ab, … See `pathSlotIdForIndex`. */
export type PathSlotId = string;

/**
 * How this cycle's paths name their stops (canon 29 §2.1 — a Path is "drawn
 * from the org chart (anchored at `orgScopeNodeId`) or named explicitly as a
 * custom participant set").
 *
 *  - 'org_chart' — stops are org units under the cycle anchor.
 *  - 'custom'    — stops are named recipients (users or groups) in an order the
 *    designer chooses. Works in workspaces with no org chart at all.
 *
 * Cycle-level, not per-path: it decides what a stop *is*, and the launched
 * `CatchballVirtualConfig.setupMode` it maps to is one row per cycle.
 */
export type PathMode = 'org_chart' | 'custom';

export type ResponseType = 'options' | 'feasibility' | 'trade-offs' | 'sequencing';

export type StartCondition = 'immediate' | 'scheduled' | 'manual';

/**
 * How the cadence calendar is anchored (V2MOM lesson: "the method arrives
 * with its calendar").
 *  - 'start': forward from the start date — level due dates accumulate downward
 *    from launch day.
 *  - 'lock':  T-minus from the end date — the last level lands on the lock
 *    date (T-0) and every level above is counted back from it, telling the
 *    anchor when the ball must leave the top.
 */
export type CadenceAnchor = 'start' | 'lock';

export interface TrackParams {
  deadlineDays: number;
  legitimacy: LegitimacyMarking;
  responseType: ResponseType;
  guidance: string;
  startCondition: StartCondition;
  notifyOnProposal: boolean;
  escalateAfterDays: number;
}

export interface DesignerPath {
  id: PathSlotId;
  name: string;
  color: string;
  /** Ordered org unit ids from anchor → endpoint */
  nodes: string[];
  params: TrackParams;
  /**
   * Optional explicit assignee per node id (org unit id → user or group).
   * A node with no entry here falls back to "anyone valid at that org node"
   * (owner ruling 2026-07-09) — the existing user_node_assignments /
   * group_node_assignments resolution at request-send time.
   */
  nodeAssignments?: Record<string, CatchballParticipantRef>;
  /**
   * Optional per-node response window override in days (org unit id → days).
   * A node with no entry inherits the path default (params.deadlineDays).
   */
  nodeWindows?: Record<string, number>;
}

export interface OrgPathNode {
  id: string;
  name: string;
  title: string;
  /** Depth relative to cycle org anchor (0 = anchor) */
  relativeLevel: number;
  parentId: string | null;
  isLeaf: boolean;
}

export interface CycleDesignerFormState {
  cycleName: string;
  strategicIntent: string;
  startDate: string;
  endDate: string;
  cycleType: CycleDesignerType;
  maxIterations: number;
  /** Optional (older drafts lack it); undefined means 'start'. */
  cadenceAnchor?: CadenceAnchor;
  /** Optional (older drafts lack it); undefined means 'org_chart'. */
  pathMode?: PathMode;
}

export type DesignerStep = 1 | 2 | 3 | 4;

export const DEFAULT_TRACK_PARAMS: TrackParams = {
  deadlineDays: 5,
  legitimacy: 'proposed',
  responseType: 'trade-offs',
  guidance: '',
  startCondition: 'immediate',
  notifyOnProposal: true,
  escalateAfterDays: 3,
};

/**
 * There is no cap on paths.
 *
 * This was a fixed list — four slots, then six — because the palette was a
 * `Record` keyed by a string union, so "how many colours are defined" silently
 * became "how many paths you may have". A cycle that runs one path per committee
 * needs a slot per committee, and nothing in the DB ever constrained slot ids:
 * they are client-side identifiers. So the palette is now a function of the
 * index and the ids are generated on demand.
 */
const PATH_PALETTE = [
  '#D4A017',
  '#3A7D56',
  '#C0533A',
  '#6B5B8C',
  '#3D4B6F',
  '#A28970',
] as const;

/**
 * Slot id for the nth path: a…z, then aa, ab, … Stable for a given index, so a
 * saved draft keeps its colours and names across reloads.
 */
export function pathSlotIdForIndex(index: number): PathSlotId {
  if (index < 0) return 'a';
  let n = index;
  let id = '';
  do {
    id = String.fromCharCode(97 + (n % 26)) + id;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return id;
}

/**
 * Colour for the nth path. The first six are the hand-picked palette; beyond
 * that, hues are walked by the golden angle so neighbouring paths stay
 * distinguishable however many there are.
 */
export function pathColorForIndex(index: number): string {
  if (index < PATH_PALETTE.length) return PATH_PALETTE[index]!;
  const hue = Math.round((index * 137.508) % 360);
  return `hsl(${hue} 42% 42%)`;
}

/** Index a slot id maps back to — inverse of `pathSlotIdForIndex`. */
export function indexForPathSlotId(id: PathSlotId): number {
  let n = 0;
  for (const ch of id) {
    const v = ch.charCodeAt(0) - 96;
    if (v < 1 || v > 26) return -1;
    n = n * 26 + v;
  }
  return n - 1;
}

export function isPathSlotId(value: string): boolean {
  return /^[a-z]+$/.test(value) && indexForPathSlotId(value) >= 0;
}

export function pathColorForSlot(id: PathSlotId): string {
  return pathColorForIndex(Math.max(0, indexForPathSlotId(id)));
}

/**
 * The first slots, for callers that want a starting order. NOT a limit — use
 * `pathSlotIdForIndex` for anything beyond, and never treat this length as a cap.
 */
export const PATH_SLOT_ORDER: PathSlotId[] = Array.from({ length: PATH_PALETTE.length }, (_, i) =>
  pathSlotIdForIndex(i),
);

/** Back-compat colour lookup for the seeded slots. */
export const PATH_COLORS: Record<PathSlotId, string> = Object.fromEntries(
  PATH_SLOT_ORDER.map((id) => [id, pathColorForSlot(id)]),
);
